import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchFuelWithCache,
  purgeFuelCache,
} from '../services/fuelClientCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * useFuelData — Phase 2 Structural + Phase 3 UX (audit doc 11.06.2026)
 * --------------------------------------------------------------------
 * Centralised React hook for fetching fuel data. Wraps fetchFuelWithCache
 * so every consumer (FuelPrices.js, FuelNearbyCard.js, future widgets)
 * shares the same:
 *
 *   • localStorage cache (Phase 1)
 *   • timeout + exponential-backoff retries (Phase 1+2)
 *   • stale-while-revalidate background refresh (Phase 2)
 *   • session retry budget (Phase 2)
 *   • categorised errors (Phase 3) — { errorKind: 'timeout'|'network'|'server'|'client'|'unknown' }
 *   • ETag / If-None-Match handling (Phase 3, transparent inside the cache module)
 *
 * The hook owns:
 *   • data            — the parsed JSON response (or null while loading first time)
 *   • loading         — true ONLY for the very first fetch when no cache exists.
 *   • error           — non-null when the most-recent fetch failed
 *   • errorKind       — categorised error string (Phase 3)
 *   • fromCache       — true if the current `data` was served from cache
 *   • stale           — true if `data` is cached AND the live fetch failed
 *   • fetchedAt       — epoch ms of when `data` was originally cached/fetched
 *   • justRefreshed   — Phase 3: pulses true for ~1s after a successful
 *                       background revalidation that brought new bytes.
 *                       Lets the UI flash a "fresh!" badge.
 *   • refresh(opts?)  — force a network fetch, purging the matching cache key
 *
 * @param {string} endpoint
 * @param {object|null} params
 * @param {object} options
 *   - cachePrefix: string
 *   - enabled:     bool (default true)
 *   - fetchOptions: forwarded to fetch()
 */
export default function useFuelData(endpoint, params, options = {}) {
  const { cachePrefix = '', enabled = true, fetchOptions = {} } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [stale, setStale] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(0);
  const [justRefreshed, setJustRefreshed] = useState(false);

  const requestSeq = useRef(0);
  const lastFetchedAtRef = useRef(0);

  const buildQuery = useCallback((p) => {
    if (!p) return '';
    const qp = new URLSearchParams();
    Object.entries(p).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') qp.append(k, String(v));
    });
    const s = qp.toString();
    return s ? `?${s}` : '';
  }, []);

  const doFetch = useCallback(
    async (forceRefresh = false) => {
      if (!enabled || !endpoint) return;
      const query = buildQuery(params);
      const url = `${BACKEND_URL}${endpoint}${query}`;
      const cacheKey = `${cachePrefix}:${endpoint}${query}`;

      const mySeq = requestSeq.current + 1;
      requestSeq.current = mySeq;

      setLoading((prev) => (data === null ? true : prev));

      const result = await fetchFuelWithCache(cacheKey, url, {
        forceRefresh,
        fetchOptions,
      });

      if (mySeq !== requestSeq.current) return;

      setFromCache(result.fromCache);
      setStale(result.stale);
      setFetchedAt(result.fetchedAt);
      if (result.data) {
        setData(result.data);
        setError(result.error || null);
        setErrorKind(result.errorKind || null);
      } else if (result.error) {
        setError(result.error);
        setErrorKind(result.errorKind || null);
      }
      setLoading(false);
    },
    [endpoint, params, cachePrefix, enabled, fetchOptions, buildQuery, data]
  );

  const paramsKey = params ? JSON.stringify(params) : '';
  useEffect(() => {
    doFetch(false);
  }, [endpoint, paramsKey, enabled]); // eslint-disable-line

  // Phase 3: detect a successful background revalidation that brought NEW
  // bytes (fetchedAt advanced while data was already showing). Pulse a
  // 1.2s "justRefreshed" flag the UI can render a small badge against.
  useEffect(() => {
    if (fetchedAt && lastFetchedAtRef.current && fetchedAt > lastFetchedAtRef.current) {
      setJustRefreshed(true);
      const t = setTimeout(() => setJustRefreshed(false), 1200);
      lastFetchedAtRef.current = fetchedAt;
      return () => clearTimeout(t);
    }
    if (fetchedAt && !lastFetchedAtRef.current) {
      lastFetchedAtRef.current = fetchedAt;
    }
    return undefined;
  }, [fetchedAt]);

  // Phase 3: poll localStorage cache freshness while the hook is mounted so
  // SWR-driven background refreshes propagate to UI without a remount.
  useEffect(() => {
    if (!enabled || !endpoint) return undefined;
    const interval = setInterval(() => {
      const query = buildQuery(params);
      const cacheKey = `${cachePrefix}:${endpoint}${query}`;
      try {
        const raw = window.localStorage.getItem(`pricepal_fuel_cache_v1::${cacheKey}`);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed?.timestamp && parsed.timestamp > lastFetchedAtRef.current) {
          setData(parsed.data);
          setFetchedAt(parsed.timestamp);
        }
      } catch {
        // ignore
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [endpoint, paramsKey, cachePrefix, enabled, buildQuery, params]);

  const refresh = useCallback(
    (opts = {}) => {
      const { hard = true } = opts;
      if (hard) purgeFuelCache(`${cachePrefix}:`);
      return doFetch(true);
    },
    [cachePrefix, doFetch]
  );

  return {
    data,
    loading,
    error,
    errorKind,
    fromCache,
    stale,
    fetchedAt,
    justRefreshed,
    refresh,
  };
}
